import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ChannelCategoryService } from '@service/channel/channel-category.service';
import { ChannelGroupService } from '@service/channel/channel-group.service';
import { ChannelSheetService } from '@service/channel/channel-sheet.service';
import { TranslateService } from '@ngx-translate/core';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { ChannelGroup } from 'icreate/common/models/channel-group';
import { ToastrService } from 'ngx-toastr';
import { Subject, lastValueFrom, take, takeUntil } from 'rxjs';
import { ChannelSheetComponent } from '@components/channel/channel-sheet/channel-sheet.component';
import { VesselService } from '@service/vessel.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Vessel } from 'icreate/common/models/vessel';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { ReposeSettingComponent } from './repose-setting/repose-setting.component';
import { RunningHourSettingComponent } from './running-hour-setting/running-hour-setting.component';
import { ExhaustGasSettingComponent } from './exhaust-gas-setting/exhaust-gas-setting.component';
import { SensorCableTagComponent } from './sensor-cable-tag/sensor-cable-tag.component';
import { AlarmGroupSettingComponent } from './alarm-group-setting/alarm-group-setting.component';
import { AuthService } from '@service/auth.service';
import { ContactService } from '@service/contact.service';
import { AutosaveErrorDialogComponent } from '@components/autosave-error-dialog/autosave-error-dialog.component';
import dayjs from 'dayjs';
import { MatDialog } from '@angular/material/dialog';
import { VesselSocketService } from '@service/vessel-socket.service';
import { AutosaveControlService, AutosavePage } from '@service/auto-save-controller.service';

export enum Tabs {
    DEFAULT = 'default',
    ALARM_GROUP = 'alarm-group',
    REPOSE = 'repose',
    RUNNING_HOUR = 'running-hour',
    EXHAUST_GAS = 'exhaust-gas',
    SENSOR_CABLE = 'sensor-cable',
    LOGIC = 'logic',
}

@Component({
    selector: 'app-channel',
    templateUrl: './channel.component.html',
    styleUrls: ['./channel.component.scss'],
    standalone: false,
})
export class ChannelComponent implements OnInit, OnDestroy {
    readonly excelImportError = 'excel-channel-import.errors';

    readonly TAB_DEFAULT = Tabs.DEFAULT;
    readonly TAB_ALARM_GROUP = Tabs.ALARM_GROUP;
    readonly TAB_REPOSE = Tabs.REPOSE;
    readonly TAB_RUNNING_HOUR = Tabs.RUNNING_HOUR;
    readonly TAB_EXHAUST_GAS = Tabs.EXHAUST_GAS;
    readonly TAB_SENSOR_CABLE = Tabs.SENSOR_CABLE;
    readonly TAB_LOGIC = Tabs.LOGIC;

    @ViewChild('channelSheet') channelSheet: ChannelSheetComponent;
    @ViewChild('exitPageConfirmation')
    exitPageConfirmation: ConfirmationWindowComponent;
    @ViewChild('alarmGroupSetting')
    alarmGroupSetting: AlarmGroupSettingComponent;
    @ViewChild('reposeSetting') reposeSetting: ReposeSettingComponent;
    @ViewChild('runningHourSetting')
    runningHourSetting: RunningHourSettingComponent;
    @ViewChild('exhaustGasSetting')
    exhaustGasSetting: ExhaustGasSettingComponent;
    @ViewChild('sensorCableTag') sensorCableTag: SensorCableTagComponent;

    showPannel: boolean = false;
    tableWidth = 0;

    channelCategoryList: ChannelCategory[] = [];
    channelGroupList: ChannelGroup[] = [];
    isSheetLoading: boolean = true;
    selectedFile: File;

    vessel: Vessel;

    selectedTab: Tabs = this.TAB_DEFAULT;
    isSaveEnabled = false;

    private shipyardId: string;
    private vesselId: string;
    private destroySubscriptionSubject$ = new Subject<void>();

    userCanEdit: boolean = true;
    isAutosaveEnabled: boolean = false;
    autosaveStatus: AutoSaveStatus = AutoSaveStatus.UNDEFINED;
    lastSaveTime: string;
    saveInterval;
    currentUser: { name: string; oid: string; email: string };

    constructor(
        private channelGroupService: ChannelGroupService,
        private vesselService: VesselService,
        private vesselSocketService: VesselSocketService,
        private channelSheetService: ChannelSheetService,
        private toastr: ToastrService,
        private channelCategoryService: ChannelCategoryService,
        private router: Router,
        private route: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        private translateService: TranslateService,
        private dialog: MatDialog,
        private contactService: ContactService,
        private authService: AuthService,
        private autosaveControlService: AutosaveControlService
    ) {}

    async ngOnInit() {
        this.currentUser = this.authService.getCurrentUser();

        const state = history.state;
        const stateTab = state?.origin;
        if (stateTab) {
            this.selectedTab = stateTab;
            const { origin: _, ...updatedState } = state;
            history.replaceState(updatedState, '');
        }

        this.route.params.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(async params => {
            this.shipyardId = params['shipyardId'];
            this.vesselId = params['vesselId'];

            const currentVessel: any = await this.vesselService.getVessel(this.vesselId);
            if (!currentVessel) {
                this.router.navigateByUrl('home');
                return;
            }
            await this.initVessel(currentVessel);
            this.cdr.detectChanges();
        });

        this.channelSheetService.loadingEmitter.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(loading => {
            this.isSheetLoading = loading;
            this.cdr.detectChanges();
        });

        this.vesselService.accessUpdated$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(canEdit => {
            this.userCanEdit = canEdit;
            this.isAutosaveEnabled = this.selectedTab == this.TAB_DEFAULT && canEdit;
            if (this.isAutosaveEnabled) {
                this.startSaveInterval();
            }
            this.cdr.detectChanges();
        });

        this.vesselSocketService
            .vesselUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async (updatedVessel: Vessel) => {
                if (this.vessel.id === updatedVessel.id) {
                    await this.initVessel(updatedVessel);
                    this.cdr.detectChanges();
                }
            });

        this.dialog.afterOpened.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(() => {
            this.autosaveControlService.setDialogOpen(AutosavePage.CHANNEL, true);
        });

        this.dialog.afterAllClosed.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(() => {
            this.autosaveControlService.setDialogOpen(AutosavePage.CHANNEL, false);
        });

        this.autosaveControlService
            .getDialogState(AutosavePage.CHANNEL)
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(isOpen => {
                if (isOpen) {
                    this.stopSaveInterval();
                } else if (this.selectedTab === this.TAB_DEFAULT && this.isAutosaveEnabled) {
                    this.startSaveInterval();
                }
            });

        this.cdr.detectChanges();
    }

    ngOnDestroy() {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();

        if (this.saveInterval) {
            clearInterval(this.saveInterval);
            this.saveInterval = null;
        }
    }

    showErrorMsg(msg: string) {
        this.toastr.error(msg, '', {
            timeOut: 4000,
            closeButton: true,
        });
    }

    async initVessel(currentVessel) {
        this.vessel = currentVessel;

        this.vesselService.setActiveVessel(this.vessel);

        await this.channelGroupService.getList(this.vesselId);
        await this.channelCategoryService.getList(this.vesselId);
    }

    async save(autoSave: boolean = false) {
        if (!this.userCanEdit) return;
        let hasErrorOnSave = false;
        try {
            if (this.selectedTab === this.TAB_DEFAULT) {
                hasErrorOnSave = await this.channelSheet.save(autoSave);
            } else if (this.selectedTab === this.TAB_RUNNING_HOUR) {
                await this.runningHourSetting.save();
            } else if (this.selectedTab === this.TAB_EXHAUST_GAS) {
                await this.exhaustGasSetting.save();
            } else if (this.selectedTab === this.TAB_SENSOR_CABLE) {
                await this.sensorCableTag.save();
            } else if (this.selectedTab === this.TAB_REPOSE) {
                await this.reposeSetting.save();
            } else if (this.selectedTab === this.TAB_ALARM_GROUP) {
                await this.alarmGroupSetting.save();
            }
            if (!hasErrorOnSave && !autoSave) {
                this.isSaveEnabled = false;
            }
        } catch (error) {
            throw error;
        }
    }

    async cancel() {
        if (this.isSaveEnabled) {
            await this.showExitConfirmationDialog();
        } else {
            this.router.navigateByUrl(`home/${this.shipyardId}/${this.vesselId}`);
        }
    }

    async showExitConfirmationDialog(returnHome: boolean = true) {
        this.exitPageConfirmation.setVisible(true, true, AutosavePage.CHANNEL);

        const confirmationOption = await lastValueFrom(this.exitPageConfirmation.onOptionSelected.pipe(take(1)));

        if (confirmationOption === ConfirmationWindowTypes.DISCARD) {
            if (returnHome) {
                this.router.navigateByUrl(`home/${this.shipyardId}/${this.vesselId}`);
            }
            return true;
        }

        if (confirmationOption === ConfirmationWindowTypes.SAVE) {
            await this.save();

            if (returnHome) {
                this.router.navigateByUrl(`home/${this.shipyardId}/${this.vesselId}`);
            }
            return true;
        }

        return false;
    }

    async tabChange(tab: Tabs) {
        if (this.isSaveEnabled) {
            const continueTabChange = await this.showExitConfirmationDialog(false);

            if (!continueTabChange) return;
        }
        this.stopSaveInterval();

        this.isSaveEnabled = false;
        this.selectedTab = tab;
        this.channelGroupService.getList(this.vessel.id);
        this.isAutosaveEnabled = this.selectedTab === this.TAB_DEFAULT;
        this.lastSaveTime = null;
        this.autosaveStatus = undefined;
        if (this.isAutosaveEnabled && this.userCanEdit) {
            this.startSaveInterval();
        } else {
            this.isAutosaveEnabled = false;
        }
    }

    handleExitConfirmation(message: string) {
        let confirmMessage = this.translateService.instant(message);
        const confirmLeave = confirm(confirmMessage);
        return confirmLeave;
    }

    onPageUpdated(value: boolean) {
        this.isSaveEnabled = value;
    }

    updateAutosave(event) {
        this.isAutosaveEnabled = event.checked;
        if (this.isAutosaveEnabled) this.startSaveInterval();
        else {
            clearInterval(this.saveInterval);
            this.saveInterval = null;
        }
    }

    getAutosaveIcon() {
        if (
            (this.autosaveStatus == AutoSaveStatus.SAVING || this.autosaveStatus == AutoSaveStatus.DELAYED) &&
            this.lastSaveTime
        ) {
            return 'loading-save';
        } else if (this.autosaveStatus == AutoSaveStatus.SAVED) {
            return 'tick';
        } else if (this.autosaveStatus == AutoSaveStatus.ERROR) {
            return 'save-error';
        }
    }

    getAutosaveStatus() {
        if (this.autosaveStatus == AutoSaveStatus.SAVING) {
            return this.translateService.instant('channel.header.autosave.status.saving');
        } else if (this.autosaveStatus == AutoSaveStatus.DELAYED) {
            return this.translateService.instant('channel.header.autosave.status.delayed');
        } else if (this.autosaveStatus == AutoSaveStatus.SAVED && this.lastSaveTime) {
            return this.translateService.instant('channel.header.autosave.status.saved', {
                time: this.lastSaveTime,
            });
        } else if (this.autosaveStatus == AutoSaveStatus.ERROR) {
            return this.translateService.instant('channel.header.autosave.status.error');
        }
    }

    startSaveInterval() {
        if (
            !this.isAutosaveEnabled ||
            this.saveInterval ||
            !this.userCanEdit ||
            this.selectedTab !== this.TAB_DEFAULT
        ) {
            return;
        }

        this.saveInterval = setInterval(async () => {
            this.autoSaveChannels();
        }, 60000);
    }

    stopSaveInterval() {
        if (!this.saveInterval) return;

        clearInterval(this.saveInterval);
        this.saveInterval = null;
    }

    async autoSaveChannels() {
        if (this.autosaveStatus === AutoSaveStatus.SAVING || !this.userCanEdit) {
            return;
        }

        const delayTimeout = setTimeout(() => {
            if (this.autosaveStatus === AutoSaveStatus.SAVING) {
                this.autosaveStatus = AutoSaveStatus.DELAYED;
            }
        }, 300000);

        try {
            this.autosaveStatus = AutoSaveStatus.SAVING;

            await this.save(true);

            this.autosaveStatus = AutoSaveStatus.SAVED;

            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const hour = dayjs().hour();
            const minutes = dayjs().minute().toString().padStart(2, '0');

            this.lastSaveTime = `${hour}:${minutes} ${timezone}`;
        } catch (err) {
            this.autosaveStatus = AutoSaveStatus.ERROR;
            console.error(err);
        } finally {
            clearTimeout(delayTimeout);
        }
    }

    updateSaveTime() {
        this.autosaveStatus = AutoSaveStatus.SAVED;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const hour = dayjs().hour();
        const minutes = dayjs().minute().toString().padStart(2, '0');
        this.lastSaveTime = hour + ':' + minutes + ' ' + timezone;
    }

    hasSaveError() {
        return this.autosaveStatus === AutoSaveStatus.ERROR;
    }

    async openErrorMessage() {
        const dialogRef = this.dialog.open(AutosaveErrorDialogComponent, {
            width: '442px',
            data: {
                projectVersion: this.vesselService.getActiveVessel().projectVersion,
            },
        });
        const shouldSendError = await lastValueFrom(dialogRef.afterClosed());

        if (shouldSendError) {
            try {
                const receiverAddress = this.currentUser.email;
                await this.contactService.sendErrorReport(receiverAddress);
            } catch (err) {
                console.error(err);
            }
        }
    }

    getMessageName() {
        if (this.selectedTab === this.TAB_DEFAULT) {
            return this.translateService.instant('channel-sheet.tab.alarm');
        } else if (this.selectedTab === this.TAB_ALARM_GROUP) {
            return this.translateService.instant('channel-sheet.tab.alarm-group');
        } else if (this.selectedTab === this.TAB_REPOSE) {
            return this.translateService.instant('channel-sheet.tab.repose');
        } else if (this.selectedTab === this.TAB_RUNNING_HOUR) {
            return this.translateService.instant('channel-sheet.tab.running-hour');
        } else if (this.selectedTab === this.TAB_EXHAUST_GAS) {
            return this.translateService.instant('channel-sheet.tab.exhaust-gas');
        } else if (this.selectedTab === this.TAB_SENSOR_CABLE) {
            return this.translateService.instant('channel-sheet.tab.sensor-cable-tag');
        }
    }
}

export enum AutoSaveStatus {
    UNDEFINED = 'undefined',
    SAVING = 'saving',
    SAVED = 'saved',
    DELAYED = 'delayed',
    ERROR = 'error',
}
